import React, { useEffect, useState } from "react";

/* ================= CONFIG ================= */
// ✅ FIXED
const API = process.env.REACT_APP_API_URL || "/api";

const DeviceHealth = () => {
  const [devices, setDevices] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  /* ================= FETCH ================= */
  const fetchDevices = async () => {
    try {
      const res = await fetch(`${API}/devices`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await res.json();

      // ✅ SAFE PARSE
      const safeData = Array.isArray(data)
        ? data
        : data.rows || data.data || [];

      setDevices(safeData);
      setLastUpdated(new Date());

    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    fetchDevices();

    const interval = setInterval(fetchDevices, 10000);
    return () => clearInterval(interval);
  }, []);

  /* ================= KPI ================= */
  const online = devices.filter(d => d.status === "online").length;
  const offline = devices.filter(d => d.status === "offline").length;
  const maintenance = devices.filter(d => d.status === "maintenance").length;

  if (initialLoad) {
    return <div className="p-6">Loading device health...</div>;
  }

  return (
    <div className="p-6 space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold text-slate-800">
          Device Health
        </h1>

        <div className="text-xs text-gray-400">
          Last refresh: {lastUpdated?.toLocaleTimeString()}
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-6">
        <Stat title="Online Devices" value={online} color="green" />
        <Stat title="Offline Devices" value={offline} color="red" />
        <Stat title="Maintenance" value={maintenance} color="yellow" />
      </div>

      {/* DEVICE GRID */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">

        {devices.map((device) => (
          <div
            key={device.id}
            className="bg-white p-4 rounded-xl border hover:shadow transition"
          >
            <div className="font-semibold text-slate-700">
              {device.device_uid}
            </div>

            <div className="text-xs text-slate-400">
              {device.product_name || "No Product"}
            </div>

            <div className="mt-3 flex items-center gap-2">
              <StatusDot status={device.status} />
              <StatusBadge status={device.status} />
            </div>

            <div className="text-xs text-slate-400 mt-2">
              Last Seen:
              <br />
              {device.last_seen_text || "No Data"}
            </div>

            <div className="text-xs text-slate-500 mt-2">
              CO₂: {device.co2 ?? 0}
            </div>
          </div>
        ))}

        {devices.length === 0 && (
          <div className="col-span-full text-center text-gray-400">
            No devices available
          </div>
        )}

      </div>

    </div>
  );
};

/* ================= KPI CARD ================= */

const Stat = ({ title, value, color }) => {
  const colors = {
    green: "text-green-600",
    red: "text-red-600",
    yellow: "text-yellow-600",
  };

  return (
    <div className="bg-white p-6 rounded-xl border">
      <p className="text-sm text-slate-500">{title}</p>
      <h2 className={`text-2xl font-semibold mt-2 ${colors[color]}`}>
        {value}
      </h2>
    </div>
  );
};

/* ================= STATUS DOT ================= */

const StatusDot = ({ status }) => {
  const map = {
    online: "bg-green-500",
    maintenance: "bg-yellow-500",
    offline: "bg-red-500",
  };

  return (
    <span className={`w-2 h-2 rounded-full ${map[status]}`} />
  );
};

/* ================= STATUS BADGE ================= */

const StatusBadge = ({ status }) => {
  const map = {
    online: "bg-green-100 text-green-700",
    maintenance: "bg-yellow-100 text-yellow-700",
    offline: "bg-red-100 text-red-600",
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs ${map[status]}`}>
      {status}
    </span>
  );
};

export default DeviceHealth;
